import React, { createContext, useContext, useState, useCallback } from "react";
import impersonationStore from "../utils/impersonationStore";

const ImpersonationContext = createContext(null);

/**
 * Provides impersonation state to the component tree.
 * State lives in React state (triggers re-renders) AND in the
 * module-level impersonationStore (readable by apiClient interceptor).
 */
export function ImpersonationProvider({ children }) {
  const [state, setState] = useState({
    mode: null,   // 'role' | 'user' | null
    role: null,
    userId: null,
    userName: null,
    userEmail: null,
    userRole: null,
  });

  // Sync React state → module store
  const syncStore = (next) => {
    Object.assign(impersonationStore, next);
    setState(next);
  };

  const impersonateRole = useCallback((role) => {
    syncStore({
      mode: "role",
      role,
      userId: null,
      userName: null,
      userEmail: null,
      userRole: role,
    });
  }, []);

  const impersonateUser = useCallback((user) => {
    syncStore({
      mode: "user",
      role: null,
      userId: user.id,
      userName: user.name,
      userEmail: user.email,
      userRole: user.role,
    });
  }, []);

  const exitImpersonation = useCallback(() => {
    syncStore({
      mode: null,
      role: null,
      userId: null,
      userName: null,
      userEmail: null,
      userRole: null,
    });
  }, []);

  const value = {
    ...state,
    isActive: state.mode !== null,
    /** The effective role to use for nav/route checks */
    effectiveRole: state.mode ? state.userRole : null,
    impersonateRole,
    impersonateUser,
    exitImpersonation,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) {
    throw new Error("useImpersonation must be used within ImpersonationProvider");
  }
  return ctx;
}
